/**
 * Navigation item interface
 */
export interface NavigationItem {
  /** Route path */
  path: string;
  /** Display label */
  label: string;
  /** Icon name */
  icon?: string;
  /** Priority for ordering (lower = higher priority) */
  priority: number;
  /** Whether this is a primary route for the role */
  isPrimary?: boolean;
  /** Whether this route should be excluded from fallbacks */
  excludeFromFallback?: boolean;
}

/**
 * Navigation configuration for each user role
 */
export const ROLE_NAVIGATION_CONFIG: Record<string, NavigationItem[]> = {
  patient: [
    {
      path: '/home',
      label: 'Home',
      icon: 'home',
      priority: 1,
      isPrimary: true,
    },
    {
      path: '/my-appointments',
      label: 'I miei appuntamenti',
      icon: 'calendar',
      priority: 2,
    },
    {
      path: '/my-pathway',
      label: 'Il mio percorso',
      icon: 'route',
      priority: 3,
    },
    {
      path: '/documents',
      label: 'Documenti',
      icon: 'file-text',
      priority: 4,
    },
    {
      path: '/profile',
      label: 'Profilo',
      icon: 'user',
      priority: 5,
    },
  ],
  
  doctor: [
    {
      path: '/agenda',
      label: 'Agenda',
      icon: 'calendar',
      priority: 1,
      isPrimary: true,
    },
    {
      path: '/my-patients',
      label: 'I miei pazienti',
      icon: 'users',
      priority: 2,
    },
    {
      path: '/my-appointments',
      label: 'Appuntamenti',
      icon: 'clock',
      priority: 3,
    },
    {
      path: '/documents',
      label: 'Documenti',
      icon: 'file-text',
      priority: 4,
    },
    {
      path: '/profile',
      label: 'Profilo',
      icon: 'user',
      priority: 5,
    },
  ],
  
  psycho: [
    {
      path: '/agenda',
      label: 'Agenda',
      icon: 'calendar',
      priority: 1,
      isPrimary: true,
    },
    {
      path: '/my-patients',
      label: 'I miei pazienti',
      icon: 'users',
      priority: 2,
    },
    {
      path: '/my-appointments',
      label: 'Appuntamenti',
      icon: 'clock',
      priority: 3,
    },
    {
      path: '/documents',
      label: 'Documenti',
      icon: 'file-text',
      priority: 4,
    },
    {
      path: '/profile',
      label: 'Profilo',
      icon: 'user',
      priority: 5,
    },
  ],
  
  clinic: [
    {
      path: '/dashboard',
      label: 'Dashboard',
      icon: 'layout-dashboard',
      priority: 1,
      isPrimary: true,
    },
    {
      path: '/agenda',
      label: 'Agenda',
      icon: 'calendar',
      priority: 2,
    },
    {
      path: '/bookings',
      label: 'Prenotazioni',
      icon: 'calendar-check',
      priority: 3,
    },
    {
      path: '/service-management',
      label: 'Gestione servizi',
      icon: 'settings',
      priority: 4,
    },
    {
      path: '/doctors',
      label: 'Medici',
      icon: 'user-doctor',
      priority: 5,
    },
    {
      path: '/reports',
      label: 'Report',
      icon: 'chart-bar',
      priority: 6,
    },
    {
      path: '/profile',
      label: 'Profilo',
      icon: 'building',
      priority: 7,
    },
  ],
  
  admin: [
    {
      path: '/admin',
      label: 'Pannello Admin',
      icon: 'shield',
      priority: 1,
      isPrimary: true,
    },
    {
      path: '/users',
      label: 'Gestione utenti',
      icon: 'users',
      priority: 2,
    },
    {
      path: '/clinics',
      label: 'Gestione cliniche',
      icon: 'building-hospital',
      priority: 3,
    },
    {
      path: '/system',
      label: 'Sistema',
      icon: 'server',
      priority: 4,
    },
  ],
};

/**
 * Routes that should never be used as fallback destinations
 */
export const BLACKLISTED_ROUTES = [
  '/google-auth-callback',
  '/google-calendar-callback',
  '/complete-registration',
  '/auth-flow',
  '/login',
  '/register',
  '',
];

/**
 * Get navigation items for a specific role
 */
export function getRoleNavigation(role: string): NavigationItem[] {
  return ROLE_NAVIGATION_CONFIG[role] || [];
}

/**
 * Get primary route for a specific role
 */
export function getPrimaryRoute(role: string): string {
  const navigation = getRoleNavigation(role);
  const primary = navigation.find(item => item.isPrimary);
  return primary?.path || navigation[0]?.path || '/home';
}

/**
 * Get allowed navigation paths for a role
 */
export function getAllowedPaths(role: string): string[] {
  return getRoleNavigation(role).map(item => item.path);
}

/**
 * Check if a user with given role can access a specific path
 */
export function canAccessPath(path: string, role: string): boolean {
  const allowedPaths = getAllowedPaths(role);
  const normalizedPath = path.split('?')[0].split('#')[0].toLowerCase();
  
  // Always allow access to auth-related and common routes
  const commonRoutes = ['/profile', '/logout', '/complete-registration', '/auth-flow'];
  if (commonRoutes.some(route => normalizedPath.startsWith(route))) {
    return true;
  }
  
  return allowedPaths.some(allowedPath => 
    normalizedPath === allowedPath.toLowerCase() ||
    normalizedPath.startsWith(allowedPath.toLowerCase() + '/')
  );
}

/**
 * Get fallback routes for a role (excluding blacklisted and recently visited)
 */
export function getFallbackRoutes(role: string, excludePaths: string[] = []): string[] {
  const navigation = getRoleNavigation(role);
  const excludeNormalized = excludePaths.map(path => 
    path.split('?')[0].split('#')[0].toLowerCase()
  );
  
  return navigation
    .filter(item => 
      !item.excludeFromFallback &&
      !BLACKLISTED_ROUTES.includes(item.path) &&
      !excludeNormalized.includes(item.path.toLowerCase())
    )
    .sort((a, b) => a.priority - b.priority)
    .map(item => item.path);
}

/**
 * Get navigation items sorted by priority
 */
export function getSortedNavigation(role: string): NavigationItem[] {
  const navigation = getRoleNavigation(role);
  return navigation.sort((a, b) => a.priority - b.priority);
}